import { create } from 'zustand';
import {
  Event,
  ServerMessage,
  GameStateData,
  QuestionOpenData,
  QueueEntry,
  ScoreEntry,
  AnswerResultData,
  CalledOnData,
  WelcomeData,
  ErrorData,
} from './shared/protocol';
import { updatePlayerSession } from './session';

interface GameStore {
  connected: boolean;
  state: GameStateData | null;
  playerId: string | null;
  calledOn: CalledOnData | null;
  lastResult: AnswerResultData | null;
  error: string | null;

  setConnected: (c: boolean) => void;
  apply: (msg: ServerMessage) => void;
  clearError: () => void;
  reset: () => void;
}

export const useGameStore = create<GameStore>((set) => ({
  connected: false,
  state: null,
  playerId: null,
  calledOn: null,
  lastResult: null,
  error: null,

  setConnected: (connected) => set({ connected }),
  clearError: () => set({ error: null }),
  reset: () => set({ state: null, calledOn: null, lastResult: null, error: null }),

  apply: (msg) =>
    set((s) => {
      switch (msg.type) {
        case Event.GameState:
          return { state: msg.data as GameStateData, calledOn: null };

        case Event.Welcome: {
          const d = msg.data as WelcomeData;
          // Persist identity so reconnects re-attach to the same player/score.
          updatePlayerSession({ playerId: d.playerId, nickname: d.nickname });
          return { playerId: d.playerId };
        }

        case Event.QuestionOpen: {
          const d = msg.data as QuestionOpenData;
          if (!s.state) return s;
          return {
            state: {
              ...s.state,
              questionState: 'open',
              question: d.question,
              topicTitle: d.topicTitle,
              endsAt: d.endsAt,
              answerNote: d.answerNote ?? s.state.answerNote,
              queue: [],
            },
            calledOn: null,
            lastResult: null,
          };
        }

        case Event.QueueUpdate:
          if (!s.state) return s;
          return { state: { ...s.state, queue: msg.data as QueueEntry[] } };

        case Event.QuestionShut:
          if (!s.state) return s;
          return { state: { ...s.state, questionState: 'closed', queue: msg.data as QueueEntry[] } };

        case Event.ScoreUpdate:
          if (!s.state) return s;
          return { state: { ...s.state, scores: msg.data as ScoreEntry[] } };

        case Event.CalledOn:
          return { calledOn: msg.data as CalledOnData };

        case Event.AnswerResult:
          return { lastResult: msg.data as AnswerResultData };

        case Event.GameEnded:
          if (!s.state) return { };
          return { state: { ...s.state, status: 'finished', scores: msg.data as ScoreEntry[] } };

        case Event.Error:
          return { error: (msg.data as ErrorData).message };

        default:
          return s;
      }
    }),
}));
